import React, { createContext, useContext, useState, useEffect } from 'react';
import { technicianAuthAPI } from '../services/api';
import { forgetPushSubscription } from '../services/push';

/**
 * Sesion de la app movil. Es una identidad distinta de la del gestor: su token
 * se guarda en `technician_token` y solo da acceso a /api/tasks y /api/chat.
 *
 * Tambien entran aqui los gestores con su codigo + PIN; `technician.kind` dice
 * si es un tecnico ('technician') o un gestor ('user').
 */
const TechnicianAuthContext = createContext(null);

export function TechnicianAuthProvider({ children }) {
  const [technician, setTechnician] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('technician_token');
    if (!token) {
      setLoading(false);
      return;
    }
    technicianAuthAPI.me()
      .then((res) => setTechnician(res.data))
      .catch(() => localStorage.removeItem('technician_token'))
      .finally(() => setLoading(false));
  }, []);

  const login = async (loginCode, pin) => {
    const res = await technicianAuthAPI.login(loginCode, pin);
    const { access_token, technician: data } = res.data;
    localStorage.setItem('technician_token', access_token);
    setTechnician(data);
    return data;
  };

  const logout = async () => {
    // Antes de soltar el token: es el que autoriza a dar de baja las notificaciones
    await forgetPushSubscription();
    localStorage.removeItem('technician_token');
    setTechnician(null);
  };

  const changePin = async (currentPin, newPin) => {
    await technicianAuthAPI.changePin(currentPin, newPin);
    setTechnician((prev) => (prev ? { ...prev, must_change_pin: false } : prev));
  };

  return (
    <TechnicianAuthContext.Provider
      value={{ technician, loading, login, logout, changePin,
               isTeamLeader: !!technician?.is_team_leader }}
    >
      {children}
    </TechnicianAuthContext.Provider>
  );
}

export function useTechnicianAuth() {
  const ctx = useContext(TechnicianAuthContext);
  if (!ctx) throw new Error('useTechnicianAuth must be used within TechnicianAuthProvider');
  return ctx;
}
